<script lang="ts" setup>
  import { PaginatedResponse, RequestParams } from '@bv/api';
  import { storeToRefs } from 'pinia';
  import { ref, watch } from 'vue';

  import notificationsApi from '@/api/notifications/notifications.api';
  import NotificationEmpty from '@/components/icons/empty-state/NotificationEmpty.vue';
  import { useNotificationsStore } from '@/stores/notifications/notifications.store';
  import { infiniteScrollExpose } from '@/types/general';
  import type { notificationType } from '@/types/notifications';

  const props = defineProps<{
    type: 'unread' | 'read';
  }>();

  const emit = defineEmits<{
    (e: 'redirect', notification: notificationType): void;
    (e: 'read', notificationId: string): void;
  }>();

  const notificationsStore = useNotificationsStore();
  const { notificationsNumber } = storeToRefs(notificationsStore);
  const { setNotificationsNumber } = notificationsStore;

  const infiniteScrollRef = ref<infiniteScrollExpose<notificationType> | null>(null);

  async function itemsGetter(config?: { params?: RequestParams }): Promise<PaginatedResponse<notificationType>> {
    const res = await notificationsApi.get(config?.params, `notifications?status=${props.type}`);
    if (props.type === 'unread') {
      setNotificationsNumber(res.data.meta.total ?? 0);
    }
    return res.data;
  }

  // watch
  watch(notificationsNumber, (newVal, oldVal) => {
    if ((newVal ?? 0) > (oldVal ?? 0)) {
      infiniteScrollRef.value?.reset();
    }
  });

  defineExpose({
    reset: () => infiniteScrollRef.value?.reset(),
  });
</script>

<template>
  <div class="flex flex-col w-full">
    <bv-infinite-scroll
      ref="infiniteScrollRef"
      :items-getter="itemsGetter"
    >
      <template #default="{ items: notifications }">
        <div
          v-for="notification in notifications"
          :key="notification.id"
          class="px-6 py-2 bg-neutral-50 flex justify-between items-start border-b border-neutral-100 last:border-b-0 cursor-pointer"
          :class="{ 'bg-white': props.type === 'read' }"
          @click.stop="emit('read', notification.id)"
        >
          <div>
            <p
              class="text-xs text-base-400 font-semibold"
              @click.prevent="emit('redirect', notification)"
            >
              {{ notification.name }}
            </p>
            <p class="text-xs text-gray-600 mt-1">
              {{ notification.body }}
            </p>
            <span class="text-xs text-gray-400">
              {{ notification.timestamp }}
            </span>
          </div>
        </div>
      </template>
      <template #empty>
        <bv-empty-state
          class="h-102.5"
          :icon="{ icon: NotificationEmpty, size: 128 }"
          size="sm"
          subtitle="notifications.empty.paragraph_trans"
          title="notifications.empty.title_trans"
        />
      </template>
    </bv-infinite-scroll>
  </div>
</template>
